import type { LodgingShopContext } from "../states/Products/Lodging/types";

/**
 * The subset of the lodging shop context that identifies a hotel "product".
 * Two bookings are considered the same product when the hotel, stay dates and
 * room type all match.
 */
export type DuplicateBookingSignatureInput = Pick<
  LodgingShopContext,
  "selectedLodging" | "fromDate" | "untilDate" | "selectedRoom"
>;

/**
 * Builds a stable signature string identifying the selected hotel product.
 *
 * Uses the room's `roomId` (the room type) rather than the rate id: the rate id
 * is a per-quote opaque token that changes on every re-shop, so it would never
 * match across two booking attempts. `roomId` is stable across re-quotes.
 *
 * Returns `null` when any of the identifying fields are missing so callers can
 * fail open (never block a booking because we could not build a signature).
 */
export const buildDuplicateBookingSignature = (
  input: DuplicateBookingSignatureInput | undefined | null
): string | null => {
  const lodgingId = input?.selectedLodging?.lodging?.id?.trim();
  const fromDate = input?.fromDate?.trim();
  const untilDate = input?.untilDate?.trim();
  const roomId = input?.selectedRoom?.roomInfo?.roomId?.trim();

  if (!lodgingId || !fromDate || !untilDate || !roomId) {
    return null;
  }

  return `${lodgingId}|${fromDate}|${untilDate}|${roomId}`;
};
